import React, { useEffect, type ReactElement, type ReactNode } from 'react'
import ThemeInputDialog from 'common/ui/theme-input-dialog/ThemeInputDialog'
import { object, string } from 'yup'
import { useFormik } from 'formik'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { assign } from 'lodash'
import { editVendor } from 'features/vendor/vendor.service'
import LabeledTextfield from 'common/ui/labeled-textfield/LabeledTextfield'

import styles from './ContactDialog.module.scss'

interface ContactDialogProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const contactSchema = object({
  firstName: string().required('Obavezno polje').trim(),
  lastName: string().required('Obavezno polje').trim(),
  role: string().required('Obavezno polje').trim(),
  phone: string().required('Obavezno polje').trim(),
  email: string().email('E-mail format nije ispravan').required('Obavezno polje'),
})

const ContactDialog: React.FC<ContactDialogProps> = ({ isOpen, setIsOpen }): ReactElement => {
  const dispatch = useAppDispatch()
  const { validationErrors, vendor, editVendorStatus } = useAppSelector((state) => state.vendor)

  const formik = useFormik({
    validationSchema: contactSchema,
    initialValues: {
      firstName: vendor?.contact?.name?.first ?? '',
      lastName: vendor?.contact?.name?.last ?? '',
      role: vendor?.contact?.role ?? '',
      phone: vendor?.contact?.phone ?? '',
      email: vendor?.contact?.email ?? '',
    },
    onSubmit: async (values) => {
      await dispatch(editVendor({ contactPerson: values }))
      setIsOpen(false)
    },
  })

  useEffect(() => {
    if (validationErrors?.length) {
      formik.setErrors(assign({}, ...validationErrors.map((err) => ({ [err.type]: err.message }))))
    }
  }, [validationErrors])

  return (
    <ThemeInputDialog
      open={isOpen}
      title="Kontakt osoba"
      description="Potrebno je da unesete informacije o osobi koju će admin kontaktirati ukoliko za tim bude bilo potrebe. Ove informacije su obavezne kako bi mogli da učestvujete na tenderima."
      status={editVendorStatus}
      onClose={() => {
        setIsOpen(false)
      }}
      confirmButtonText="snimi"
      declineButtonText="izađi"
      confirmButtonAction={() => {
        formik.handleSubmit()
      }}
      confirmButtonDisabled={!formik.isValid || !formik.dirty}
      PaperProps={{
        sx: {
          width: '850px',
        },
      }}
    >
      <form className={styles.container}>
        <LabeledTextfield
          label="*Ime"
          placeholder="Ime"
          id="firstName"
          name="firstName"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.firstName && !!formik.errors.firstName}
          helperText={formik.touched.firstName && (formik.errors.firstName as ReactNode)}
        />
        <LabeledTextfield
          label="*Prezime"
          placeholder="Prezime"
          id="lastName"
          name="lastName"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.lastName && !!formik.errors.lastName}
          helperText={formik.touched.lastName && (formik.errors.lastName as ReactNode)}
        />
        <LabeledTextfield
          label="*Pozicija u firmi"
          placeholder="Pozicija"
          id="role"
          name="role"
          value={formik.values.role}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.role && !!formik.errors.role}
          helperText={formik.touched.role && (formik.errors.role as ReactNode)}
        />
        <LabeledTextfield
          label="*Broj telefona"
          placeholder="Broj telefona"
          id="phone"
          name="phone"
          value={formik.values.phone}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.phone && !!formik.errors.phone}
          helperText={formik.touched.phone && (formik.errors.phone as ReactNode)}
        />
        <LabeledTextfield
          label="*E-mail"
          placeholder="E-mail"
          id="email"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && !!formik.errors.email}
          helperText={formik.touched.email && (formik.errors.email as ReactNode)}
          style={{ gridColumn: '1 / 3' }}
        />
      </form>
    </ThemeInputDialog>
  )
}

export default ContactDialog
